import { api, ErroDaApi } from '../api/client'
import { mensagemDoErro } from '../api/erros'
import { TEXTOS } from '../lib/glossario'
import type { Sessao } from '../types/gmud'
import { gravarSessao } from './armazenamento'

/**
 * Entrada por usuário e senha.
 *
 * Emite a MESMA sessão do login institucional — o backend chama o mesmo
 * `TokenDeSessaoService.emitir`, e daqui para a frente nada distingue as duas. O que muda é
 * só como a identidade foi comprovada, e é por isso que este arquivo termina em
 * `gravarSessao` e não numa noção própria de "logado".
 */

/** O que `GET /auth/modos` responde: as formas de entrar que ESTE ambiente aceita. */
export interface ModosDeEntrada {
  entraId: boolean
  usuarioESenha: boolean
}

/**
 * Só entra quando o bloqueio chega sem mensagem.
 *
 * O texto do servidor é que manda: é ele que sabe quantos minutos faltam, e repetir o número
 * aqui criaria duas versões da mesma regra para divergirem na primeira mudança.
 */
const MUITAS_TENTATIVAS = 'Muitas tentativas seguidas. Aguarde alguns minutos antes de tentar de novo.'

const SESSAO_INCOMPLETA =
  'O servidor respondeu sem a sessão completa. Tente de novo; se persistir, avise a equipe da SSTD.'

/**
 * Pergunta ao servidor como se entra neste ambiente.
 *
 * Mesmo princípio de `/gmuds/{id}/transicoes-possiveis`: quem sabe o que é possível é o
 * servidor. Chumbar "sem Entra ID por enquanto" na tela faria o botão continuar escondido no
 * dia em que o tenant for configurado — e a descoberta viria por chamado, não por publicação.
 *
 * A resposta é lida campo a campo, e não repassada como veio: um proxy ou portal que responda
 * no lugar do backend devolve 200 com HTML, e `data.usuarioESenha` viraria `undefined`. Aqui
 * isso vira `false`, que é a leitura honesta — não sabemos de forma nenhuma de entrar.
 *
 * @returns o que este ambiente aceita
 */
export async function consultarModosDeEntrada(): Promise<ModosDeEntrada> {
  const { data } = await api.get<Partial<ModosDeEntrada> | null>('/auth/modos')
  return { entraId: data?.entraId === true, usuarioESenha: data?.usuarioESenha === true }
}

/**
 * Entra com usuário e senha e deixa a sessão gravada.
 *
 * Devolve a mensagem em vez de lançar, como `iniciarLogin`: quem chama é um formulário, e o
 * que ele precisa saber é se pode seguir ou o que escrever na tela.
 *
 * @param usuario usuário ou e-mail completo — o servidor completa o domínio quando falta
 * @param senha senha digitada, que não é registrada em lugar nenhum daqui
 * @returns nulo quando entrou; a mensagem a exibir quando não deu
 */
export async function entrarComSenha(usuario: string, senha: string): Promise<string | null> {
  try {
    const { data } = await api.post<unknown>('/auth/login', { usuario, senha })
    if (!ehSessao(data)) {
      return SESSAO_INCOMPLETA
    }
    gravarSessao({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      // Mesmo valor que a renovação e o retorno do Entra ID gravam: é o esquema de
      // autenticação, não o tipo do token. Divergir aqui faria a sessão ter formas
      // diferentes em memória conforme por onde a pessoa entrou.
      tipo: data.tipo ?? 'Bearer',
      expiraEmSegundos: data.expiraEmSegundos ?? 0,
    })
    return null
  } catch (erro) {
    return mensagemDaFalha(erro)
  }
}

/** Corpo aceitável do login: os dois tokens são obrigatórios, o resto tem valor padrão. */
type CorpoDoLogin = Partial<Sessao> & { accessToken: string; refreshToken: string }

/**
 * Meia sessão não é sessão.
 *
 * Sem o token de renovação o acesso morre em quinze minutos sem chance de renovar — o mesmo
 * caso que `capturarRetornoDoLogin` já tratava no retorno do Entra ID. E há o 200 que não é
 * sessão nenhuma: gravá-lo faria a aplicação se achar logada e levar 401 em toda tela, sem
 * ninguém ter errado a senha e sem nada explicando o quê.
 *
 * @param corpo o que o servidor respondeu
 * @returns verdadeiro quando dá para gravar
 */
function ehSessao(corpo: unknown): corpo is CorpoDoLogin {
  const sessao = corpo as Partial<Sessao> | null
  return (
    typeof sessao?.accessToken === 'string' &&
    sessao.accessToken !== '' &&
    typeof sessao.refreshToken === 'string' &&
    sessao.refreshToken !== ''
  )
}

/**
 * Traduz a recusa numa frase que ajuda quem está tentando entrar.
 *
 * A decisão é pelo `codigo` do corpo de erro, nunca pelo texto — mesmo contrato do resto da
 * aplicação. `FalhaNoLoginException` responde 401 com o código dentro, e não 429 nem 403.
 *
 * @param erro o que a chamada rejeitou
 * @returns a mensagem exibível
 */
function mensagemDaFalha(erro: unknown): string {
  if (!(erro instanceof ErroDaApi)) {
    return TEXTOS.erroGenerico
  }
  const doServidor = erro.problema.detail

  // Bloqueio por tentativas seguidas. Hoje quem responde é `RegistroDeTentativas` (cinco
  // falhas em quinze minutos) com 401 e o código abaixo — o 429 entra porque, vindo de uma
  // camada de borda, chegaria sem código nenhum e cairia na regra de credencial logo adiante,
  // mandando digitar de novo uma senha que está certa.
  if (erro.codigo === 'LOGIN_BLOQUEADO' || erro.status === 429) {
    return doServidor ?? MUITAS_TENTATIVAS
  }

  // Credencial recusada. Nem a mensagem do servidor nem esta dizem QUAL dos dois campos
  // errou, e não é economia de palavras: distinguir "usuário não existe" de "senha errada"
  // entrega de graça a lista de quem tem conta. Um 401 sem código nosso (o 401 cru do
  // contêiner, por exemplo) não chega a ser exibido: o texto dele não foi escrito para quem
  // usa o sistema, e no lugar dele vai a frase do glossário.
  if (erro.codigo === 'CREDENCIAL_INVALIDA') {
    return doServidor ?? TEXTOS.login.credencialInvalida
  }
  if (erro.status === 401 || erro.status === 422) {
    return TEXTOS.login.credencialInvalida
  }

  // LOGIN_LOCAL_INDISPONIVEL, USUARIO_INATIVO, SEM_CONEXAO: o servidor já escreveu o que
  // aconteceu e o que fazer, em português e em voz ativa.
  return mensagemDoErro(erro)
}
